import keys from 'lodash/keys';
import has from 'lodash/has';
import pickBy from 'lodash/pickBy';
import _values from 'lodash/values';
import get from 'lodash/get';
import range from 'lodash/range';
import { DEFAULT_INPUTS } from './footprint';
import units from '../../lib/units';
import {
  isElectricVehicleMileage,
  isVehicleEfficiency,
  isElectricVehicle,
  tradeInVehicleTypes,
} from '../../lib/apiKeys';
import {
  getNumericInput,
  ensuredInputValues,
  getEnsuredNumericInput,
} from './inputs';

const ensureInputLocation = (params) => {
  if (params.input_location) return params;
  return {
    ...params,
  };
};

const getDefaultsAndResults = state => ensureInputLocation(
    keys(DEFAULT_INPUTS)
      .reduce((inputs, key) => ({
        ...inputs,
        [key]: get(state.user_footprint, ['inputs', key], DEFAULT_INPUTS[key]).toString(),
      }), {}),
  );

const applyVehicleZeroMiles = (numberVehicles, allParams) => {
  const adjustedParams = { ...allParams };
  return range(numberVehicles + 1, 11).reduce((previous, n) => ({
    ...previous,
    [`input_footprint_transportation_miles${n}`]: '0',
  }), adjustedParams);
};

const computeFootprint = (state) => {
  if (!has(state.average_footprint, 'get_defaults_and_results')) {
    throw new Error('Cannot access user footprint params before retrieving average footprint.');
  }
  const values = ensuredInputValues(state, units.STANDARD, { pullMonthlyValues: false });
  const internalStateAbbreviation =
    state.average_footprint.get_defaults_and_results.internal_state_abbreviation;
  const baseInputs = {
    ...getDefaultsAndResults(state),
    input_changed: '1',
    internal_state_abbreviation: internalStateAbbreviation,
    internal_vehiclemiles:
      state.average_footprint.get_defaults_and_results.internal_vehiclemiles,
    input_population:
      state.average_footprint.get_defaults_and_results.input_population,
  };
  let allParams = keys(values).reduce((params, apiKey) => {
    let value = values[apiKey];
    if (isElectricVehicleMileage(apiKey, state)) {
      value = 0;
    } else if (isVehicleEfficiency(apiKey)) {
      value = units.convertToMpge(value, false, isElectricVehicle(apiKey, state));
    }
    return {
      ...params,
      [apiKey]: value.toString(),
    };
  }, baseInputs);
  allParams = applyVehicleZeroMiles(
    allParams.input_footprint_transportation_num_vehicles,
    allParams,
  );
  return ensureInputLocation(allParams);
};

const moreEfficientVehicleDefault = (state, forceUnitSystem) => {
  let unitSystem = forceUnitSystem;
  if (unitSystem === undefined) {
    // This MUST stay consistent with isMetric selector.
    unitSystem = state.ui.unitSystem;
  }
  const index = get(state.ui, ['tradeInVehicleIndices', 'more_efficient'], 1);
  const mpgOld = getEnsuredNumericInput(state, `input_footprint_transportation_mpg${index}`, units.STANDARD);
  const newMpg = mpgOld + 10;
  if (units.isStandard(unitSystem)) return newMpg;
  return units.convertFuelEfficiencyToMetric(newMpg, false, false);
};

const tradeInVehicleParams = state =>
_values(tradeInVehicleTypes)
  .reduce((tradeInVehicles, vehicleType) => {
    const index = get(state.ui, ['tradeInVehicleIndices', vehicleType], 1);
    const milesOld = getEnsuredNumericInput(state, `input_footprint_transportation_miles${index}`, units.STANDARD);
    const mpgOld = getEnsuredNumericInput(state, `input_footprint_transportation_mpg${index}`, units.STANDARD);
    const vehicleParams = {
      ...tradeInVehicles,
      [`input_takeaction_${vehicleType}_vehicle_miles_old`]: milesOld,
      [`input_takeaction_${vehicleType}_vehicle_mpg_old`]: mpgOld,
    };
    if (vehicleType !== tradeInVehicleTypes.MORE_EFFICIENT) return vehicleParams;

    let mpgNew = getNumericInput(state, `input_takeaction_${vehicleType}_vehicle_mpg_new`, units.STANDARD);
    if (mpgNew === undefined) {
      mpgNew = moreEfficientVehicleDefault(state, units.STANDARD);
    }
    vehicleParams[`input_takeaction_${vehicleType}_vehicle_mpg_new`] = mpgNew;
    return vehicleParams;
  }, {});

const computeTakeActionResults = state => ({
  ...(pickBy(
    state.user_footprint.compute_footprint,
    (_value, apiKey) => !/^result_takeaction_/.test(apiKey),
  )),
  ...computeFootprint(state),
  ...tradeInVehicleParams(state),
});

export default {
  computeFootprint,
  computeTakeActionResults,
  getDefaultsAndResults,
  moreEfficientVehicleDefault,
};
